import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import { Link } from 'react-router-dom';
import API_BASE_URL from '../config';
import './GuardiaPositionPrompt.css';

/**
 * Se muestra en el Inicio del coordinador.
 *  - Si no tiene asignación: lista las posiciones libres para elegir.
 *  - Si ya tiene: muestra un resumen con enlace a /coordinacion/guardias.
 *
 * Se oculta para operadores y si no hay posiciones libres y no tiene asignación.
 */
const GuardiaPositionPrompt = () => {
  const token = useSelector((s) => s.auth.token);
  const decoded = useMemo(() => { try { return jwtDecode(token); } catch { return null; } }, [token]);
  const esCoordinador = decoded?.rol === 'colaborador';

  const [miAsignacion, setMiAsignacion] = useState(undefined); // undefined = loading
  const [libres, setLibres] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  const authHdr = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}api/guardias/dashboard`, authHdr);
      setMiAsignacion(data.miAsignacion || null);
      setLibres((data.resumen || []).filter((p) => !p.coordinador));
    } catch (err) {
      console.error('GuardiaPositionPrompt:', err.response?.status || err.message);
      setMiAsignacion(null);
    }
  }, [authHdr]);

  useEffect(() => {
    if (!esCoordinador || !token) return;
    fetchData();
  }, [esCoordinador, token, fetchData]);

  if (!esCoordinador) return null;
  if (miAsignacion === undefined) return null; // aún cargando

  // ── Ya tiene asignación ──
  if (miAsignacion) {
    return (
      <div className="gpp-card gpp-card-asigned">
        <div className="gpp-card-icon"><i className="fa fa-user-shield" /></div>
        <div className="gpp-card-text">
          <h3>Estás coordinando <strong>{miAsignacion.posicionNombre}</strong></h3>
          <p>Desde ahí gestionas operadores, pasajeros en corral y más.</p>
        </div>
        <Link to="/coordinacion/guardias" className="gpp-btn gpp-btn-primary">
          Abrir panel
        </Link>
      </div>
    );
  }

  // ── No tiene asignación pero no hay libres ──
  if (libres.length === 0) return null;

  // ── No tiene asignación, hay libres: prompt para elegir ──
  const asumir = async (posicionId) => {
    setBusyId(posicionId);
    setError(null);
    try {
      await axios.post(`${API_BASE_URL}api/guardias/asignar-coordinador`, { posicionId }, authHdr);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al asumir.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="gpp-card gpp-card-pick">
      <div className="gpp-card-header">
        <h3><i className="fa fa-shield-alt" /> Elige tu posición de guardia</h3>
        <p>Selecciona la posición que vas a coordinar este turno.</p>
      </div>

      {error && <div className="gpp-error"><i className="fa fa-exclamation-circle" /> {error}</div>}

      <div className="gpp-libres-grid">
        {libres.map((p) => (
          <button
            key={p._id}
            className="gpp-libre-btn"
            onClick={() => asumir(p._id)}
            disabled={busyId !== null}
          >
            <span className="gpp-libre-name">{p.nombre}</span>
            <span className="gpp-libre-sub">Cupo {p.cupoOperadores + (p.cupoExtra || 0)}</span>
            {busyId === p._id && <span className="gpp-libre-spin">…</span>}
          </button>
        ))}
      </div>

      <Link to="/coordinacion/guardias" className="gpp-link-ghost">
        Ver todas las posiciones →
      </Link>
    </div>
  );
};

export default GuardiaPositionPrompt;
